/**
 * sky.js — gradient sky dome and distance fog.
 *
 * A dusk carnival sky: deep blue overhead falling to warm orange at the
 * horizon. Drawn as an inside-out sphere with a vertex-interpolated gradient,
 * which costs one draw call and no texture memory.
 *
 * Fog colour is matched to the horizon so distant geometry dissolves into the
 * sky rather than ending at a hard line.
 *
 * @module world/sky
 */

import * as THREE from 'three';
import { PALETTE } from '../constants.js';

const VERT = /* glsl */`
  varying vec3 vWorldPosition;
  void main() {
    vec4 worldPos = modelMatrix * vec4(position, 1.0);
    vWorldPosition = worldPos.xyz;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAG = /* glsl */`
  uniform vec3 topColor;
  uniform vec3 bottomColor;
  uniform float offset;
  uniform float exponent;
  varying vec3 vWorldPosition;
  void main() {
    float h = normalize(vWorldPosition + vec3(0.0, offset, 0.0)).y;
    float k = pow(max(h, 0.0), exponent);
    gl_FragColor = vec4(mix(bottomColor, topColor, k), 1.0);
  }
`;

/**
 * Build the sky dome and apply fog to the scene.
 * @param {THREE.Scene} scene
 * @param {{ radius?: number, top?: number, bottom?: number, fog?: boolean }} [opts]
 * @returns {THREE.Mesh} The sky dome.
 */
export function createSky(scene, opts = {}) {
  const radius = opts.radius ?? 180;
  const top = new THREE.Color(opts.top ?? PALETTE.skyTop);
  const bottom = new THREE.Color(opts.bottom ?? PALETTE.skyBottom);

  const material = new THREE.ShaderMaterial({
    uniforms: {
      topColor: { value: top },
      bottomColor: { value: bottom },
      offset: { value: 12 },
      exponent: { value: 0.62 },
    },
    vertexShader: VERT,
    fragmentShader: FRAG,
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
  });

  const sky = new THREE.Mesh(new THREE.SphereGeometry(radius, 24, 16), material);
  sky.name = 'sky';
  sky.frustumCulled = false;
  scene.add(sky);

  if (opts.fog !== false) {
    // Horizon-matched so the midway fades out instead of stopping dead.
    const fogColor = bottom.clone().lerp(top, 0.32);
    scene.fog = new THREE.Fog(fogColor.getHex(), 38, 145);
    scene.background = fogColor;
  }

  // Stars, faint enough to read as dusk rather than night.
  const starCount = 260;
  const positions = new Float32Array(starCount * 3);
  for (let i = 0; i < starCount; i++) {
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(0.15 + Math.random() * 0.85);
    const r = radius * 0.94;
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
    positions[i * 3 + 1] = r * Math.cos(phi);
    positions[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
  }
  const starGeo = new THREE.BufferGeometry();
  starGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  const stars = new THREE.Points(starGeo, new THREE.PointsMaterial({
    color: 0xffffff, size: 0.9, sizeAttenuation: true, transparent: true, opacity: 0.55, fog: false,
  }));
  stars.name = 'stars';
  stars.frustumCulled = false;
  scene.add(stars);

  sky.userData.stars = stars;
  return sky;
}
